'use client';

import { useEffect, useReducer } from 'react';
import { HERO_SEQUENCE } from './Chero.constants';
import type { HeroSequenceState } from './Chero.types';

interface UseHeroSequenceInput {
  viewportLoaded: boolean;
}

enum HeroSequenceActionType {
  Reset = 'reset',
  LogoReady = 'logoReady',
  WaveReady = 'waveReady',
  NavReady = 'navReady',
}

interface HeroSequenceAction {
  type: HeroSequenceActionType;
}

type HeroSequenceStateResolver = (
  state: HeroSequenceState,
) => HeroSequenceState;

const INITIAL_HERO_SEQUENCE_STATE: HeroSequenceState = {
  isLogoReady: false,
  isWaveReady: false,
  isNavReady: false,
};

const HERO_SEQUENCE_STATE_BY_ACTION: Record<
  HeroSequenceActionType,
  HeroSequenceStateResolver
> = {
  [HeroSequenceActionType.Reset]: () => INITIAL_HERO_SEQUENCE_STATE,
  [HeroSequenceActionType.LogoReady]: (state) => ({
    ...state,
    isLogoReady: true,
  }),
  [HeroSequenceActionType.WaveReady]: (state) => ({
    ...state,
    isWaveReady: true,
  }),
  [HeroSequenceActionType.NavReady]: (state) => ({
    ...state,
    isNavReady: true,
  }),
};

function reduceHeroSequenceState(
  state: HeroSequenceState,
  action: HeroSequenceAction,
): HeroSequenceState {
  return HERO_SEQUENCE_STATE_BY_ACTION[action.type](state);
}

export function useHeroSequence({
  viewportLoaded,
}: UseHeroSequenceInput): HeroSequenceState {
  const [sequenceState, dispatchSequenceAction] = useReducer(
    reduceHeroSequenceState,
    INITIAL_HERO_SEQUENCE_STATE,
  );

  useEffect(() => {
    if (!viewportLoaded) {
      dispatchSequenceAction({ type: HeroSequenceActionType.Reset });
      return;
    }

    dispatchSequenceAction({ type: HeroSequenceActionType.Reset });

    const logoTimer = window.setTimeout(() => {
      dispatchSequenceAction({ type: HeroSequenceActionType.LogoReady });
    }, HERO_SEQUENCE.logoDelayMs);
    const waveTimer = window.setTimeout(() => {
      dispatchSequenceAction({ type: HeroSequenceActionType.WaveReady });
    }, HERO_SEQUENCE.waveDelayMs);
    const navTimer = window.setTimeout(() => {
      dispatchSequenceAction({ type: HeroSequenceActionType.NavReady });
    }, HERO_SEQUENCE.navDelayMs);

    return () => {
      window.clearTimeout(logoTimer);
      window.clearTimeout(waveTimer);
      window.clearTimeout(navTimer);
    };
  }, [viewportLoaded]);

  return sequenceState;
}
